import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/hooks/use-toast';
import { bulkUpdateSessionFeedbacks, getSessionFeedbacks } from '@/lib/api-calls';
import { airServerErrorFrom } from '@/lib/error-handling';
import { GetSessionFeedbacksFilter, SessionFeedback } from '@airreading/common/data';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ReactNode, useState } from 'react';

interface BulkUpdateStudentAttendanceProps {
  attended: boolean;
  trigger: ReactNode;
  selectedFeedbacks: SessionFeedback[];
  onSuccess?: () => void;
  isSelectAllPagesActive?: boolean;
  filters?: GetSessionFeedbacksFilter;
}

export const BulkUpdateStudentAttendance = ({
  attended,
  trigger,
  selectedFeedbacks,
  onSuccess,
  isSelectAllPagesActive = false,
  filters,
}: BulkUpdateStudentAttendanceProps) => {
  const confirmationMessage = `This will update all selected students' sesssion attendance status to ${attended ? 'attended' : 'not attended'}. Are you sure you want to proceed?`;
  const [open, setOpen] = useState(false);
  const { mutateAsync, isPending } = useMutation({
    mutationFn: () => {
      // Use filters when selecting all pages, otherwise use specific feedbacks
      if (isSelectAllPagesActive && filters) {
        return bulkUpdateSessionFeedbacks({
          filters,
          options: {
            studentAttended: attended,
          },
        });
      }

      return bulkUpdateSessionFeedbacks(
        selectedFeedbacks.map(({ sessionId, studentId, coachId }) => {
          return {
            sessionId,
            studentId,
            coachId,
            studentAttended: attended,
            assessmentResults: [],
            action: 'adminManualAction',
          };
        }),
      );
    },
  });

  const queryClient = useQueryClient();
  const { toast } = useToast();
  const onSubmit = () => {
    return mutateAsync()
      .then(({ successSessionIds, errors }) => {
        toast({
          title: 'Updated',
          description: `Successfully updated attendance status for ${successSessionIds.length} students.`,
        });

        if (errors.length > 0) {
          toast({
            title: 'Some sessions failed to update',
            description: (
              <div>
                <ul>
                  {errors.map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                </ul>
              </div>
            ),
            variant: 'destructive',
          });
        }

        queryClient.invalidateQueries(getSessionFeedbacks());
        setOpen(false);
        onSuccess?.();
      })
      .catch((e: unknown) => {
        toast({
          title: 'Error updating attendance status',
          description: airServerErrorFrom(e)?.userFacingMessage,
          variant: 'destructive',
        });
      });
  };

  return (
    <>
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogTrigger>{trigger}</AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Update attendance status</AlertDialogTitle>
            <AlertDialogDescription>{confirmationMessage}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction>
              <Button
                loading={isPending}
                onClick={(e) => {
                  e.preventDefault();
                  onSubmit();
                }}
              >
                Update
              </Button>
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
